import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { AppSidebar } from '../../components/AppSidebar/AppSidebar';
import SearchInput from '../../components/SearchInput/SearchInput';
import {
  useCustomerProducts,
  useCustomerProduct,
  useRequestPreviewProduct,
  type CustomerProductCard,
  type CustomerProductLoadOption,
} from '../../features/customer-products/api';
import {
  uploadCustomerOrderDocument,
  useCreateCustomerOrder,
} from '../../features/customer-orders/api';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { apiErrorMessage } from '../../utils/errors';
import { parseQuantityInput } from '../../utils/formatters';
import {
  buildCustomerOrderPayload,
  fmtMoney,
  submitBlockReason,
} from '../../features/customer-products/orderForm';
import { computeOrderPricing, PricingBreakdownView } from '../../features/pricing';
import { useFuelSurcharge } from '../../features/fuel-surcharge/api';
import './customer-products.scss';
import './customer-product-order-dialog.scss';
import './per-order-po-documents.scss';
import fallbackProductImage from '../../assets/defeo-logo.png';
function useLockBodyScroll(lock = true) {
  useEffect(() => {
    if (!lock) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [lock]);
}
function getLoadOptionId(load: CustomerProductLoadOption | null | undefined) {
  return load?.orgLoadOption?.id ?? load?.orgLoadOptionId ?? '';
}

const MAX_ORDER_DOCUMENT_SIZE_BYTES = 15 * 1024 * 1024;

const ORDER_DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
];

const ORDER_DOCUMENT_ACCEPT = ORDER_DOCUMENT_EXTENSIONS.join(',');

function validateOrderDocument(file: File): string | null {
  if (file.size > MAX_ORDER_DOCUMENT_SIZE_BYTES) {
    return 'The PO document must be 15 MB or smaller.';
  }

  const lastDotIndex = file.name.lastIndexOf('.');
  const extension = lastDotIndex >= 0 ? file.name.slice(lastDotIndex).toLowerCase() : '';

  if (!ORDER_DOCUMENT_EXTENSIONS.includes(extension)) {
    return 'This PO document type is not supported.';
  }

  return null;
}

function createdOrderIdsFromCheckoutResult(result: any): string[] {
  const ids: unknown[] = Array.isArray(result?.orderIds)
    ? result.orderIds
    : Array.isArray(result?.checkout?.orders)
      ? result.checkout.orders.map((order: any) => order?.id)
      : [result?.orderId];

  const validIds = ids.filter(
    (value): value is string => typeof value === 'string' && value.trim().length > 0
  );

  return Array.from(new Set<string>(validIds));
}

function formatLocation(address: any) {
  if (!address) {
    return '';
  }
  const name = address.label || address.fullName || 'Delivery location';
  const cityState = [address.city, address.state].filter(Boolean).join(', ');
  const locationLine = [address.line1, cityState, address.postalCode].filter(Boolean).join(' · ');
  return locationLine ? `${name} — ${locationLine}` : name;
}
function sortLocations(locations: any[]) {
  return [...locations].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) {
      return -1;
    }
    if (!a.isPrimary && b.isPrimary) {
      return 1;
    }
    return String(a.label || a.fullName || a.line1 || '').localeCompare(
      String(b.label || b.fullName || b.line1 || '')
    );
  });
}
function isEnabledProduct(product: CustomerProductCard) {
  return product.hasEnabledAccess === true || product.isEnabled === true;
}
function isPreviewOnlyProduct(product: CustomerProductCard) {
  return (
    !isEnabledProduct(product) && (product.hasPreviewAccess === true || product.isPreview === true)
  );
}
type OrderingContact = {
  type?: 'SALES_REP' | 'ORG_ADMIN' | 'ORG_OWNER' | null;
  name?: string | null;
  email?: string | null;
};
export default function CustomerProductsPage() {
  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedSearch(q, 350);
  const { data, isLoading, isFetching } = useCustomerProducts({
    search: debouncedQ,
    page: 1,
    pageSize: 100,
  });
  const products = data?.products ?? [];
  const [openId, setOpenId] = useState<string | null>(null);
  const [previewProduct, setPreviewProduct] = useState<CustomerProductCard | null>(null);
  const requestPreviewProduct = useRequestPreviewProduct();
  return (
    <div className="layout">
      <AppSidebar />
      <div className="card">
        <div
          className="flex-between"
          style={{
            marginBottom: 12,
          }}
        >
          <div>
            <h2 className="card__title">Products</h2>
            <p className="card__subtitle">
              Browse products enabled for your delivery locations, including products available for
              preview.
            </p>
          </div>
        </div>
        <div
          className="card card--bleed-top"
          style={{
            paddingTop: 14,
          }}
        >
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'minmax(0, 1fr)',
              gap: 12,
            }}
          >
            <div className="form-control">
              <label>Search</label>
              <SearchInput
                placeholder="Name or description…"
                value={q}
                onValueChange={(value) => setQ(value)}
              />
            </div>
          </div>
        </div>
        <div className="card card--bleed-top">
          {isLoading ? (
            <div
              className="skeleton"
              style={{
                height: 320,
              }}
            />
          ) : products.length === 0 ? (
            <div
              className="muted"
              style={{
                padding: 16,
              }}
            >
              No enabled or preview products match your search.
            </div>
          ) : (
            <div className="cp-grid">
              {products.map((product) => {
                const enabled = isEnabledProduct(product);
                const previewOnly = isPreviewOnlyProduct(product);
                const enabledCount = Number(product.enabledLocationCount ?? 0);
                return (
                  <button
                    key={product.id}
                    type="button"
                    className={`cp-card${previewOnly ? ' cp-card--preview' : ''}`}
                    onClick={() => {
                      /*
                       * Preview-only products use
                       * the original request flow.
                       *
                       * The customer does NOT choose
                       * a location.
                       */
                      if (previewOnly) {
                        setPreviewProduct(product);
                        return;
                      }
                      setOpenId(product.id);
                    }}
                  >
                    <div className="cp-thumb">
                      <img
                        src={product.imageUrl?.trim() || fallbackProductImage}
                        alt={product.name || 'Product'}
                        onError={(e) => {
                          e.currentTarget.src = fallbackProductImage;
                        }}
                      />
                      {previewOnly && <span className="cp-preview-banner">Preview</span>}
                    </div>
                    <div className="cp-body">
                      <div className="cp-title">{product.name}</div>
                      <div className="cp-sub">{product.organizationName ?? ''}</div>
                      <div className="cp-price">
                        {previewOnly ? (
                          <span className="cp-preview-text">
                            Preview product · Click to request
                          </span>
                        ) : enabled ? (
                          <span>
                            Enabled for <strong>{enabledCount}</strong> delivery{' '}
                            {enabledCount === 1 ? 'location' : 'locations'}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {isFetching && !isLoading && (
          <div
            className="muted"
            style={{
              marginTop: 8,
            }}
          >
            Refreshing…
          </div>
        )}
      </div>
      {previewProduct && (
        <PreviewProductRequestDialog
          product={previewProduct}
          isSubmitting={requestPreviewProduct.isPending}
          onClose={() => {
            if (!requestPreviewProduct.isPending) {
              setPreviewProduct(null);
            }
          }}
          onConfirm={async () => {
            try {
              await requestPreviewProduct.mutateAsync({
                productId: previewProduct.id,
              });
              toast.success(`Your request for "${previewProduct.name}" was sent.`);
              setPreviewProduct(null);
            } catch (error) {
              toast.error(apiErrorMessage(error, 'Could not send the product request.'));
            }
          }}
        />
      )}
      {openId && <ProductDrawer id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
/** Optional attachments for one PO. A file may be used on more than one order
 * in shared mode; each upload remains a separate order-document record. */
function PoDocumentsPicker({
  id,
  files,
  onAdd,
  onRemove,
  disabled,
  description,
}: {
  id: string;
  files: File[];
  onAdd: (newFiles: File[]) => void;
  onRemove: (fileIndex: number) => void;
  disabled: boolean;
  description?: string;
}) {
  return (
    <div className="pd-po-doc-picker">
      <label htmlFor={id}>PO documents (optional)</label>
      <input
        id={id}
        className="po-document-input"
        type="file"
        multiple
        accept={ORDER_DOCUMENT_ACCEPT}
        disabled={disabled}
        onChange={(event) => {
          const selected = Array.from(event.target.files ?? []);
          // Reset so the same file may be selected again after removal.
          event.target.value = '';
          const valid: File[] = [];
          for (const file of selected) {
            const error = validateOrderDocument(file);
            if (error) {
              toast.error(`${file.name}: ${error}`);
            } else {
              valid.push(file);
            }
          }
          if (valid.length) onAdd(valid);
        }}
      />
      {files.length > 0 && (
        <ul className="pd-po-doc-picker__files">
          {files.map((file, index) => (
            <li
              className="pd-po-doc-picker__file"
              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
            >
              <span className="pd-po-doc-picker__file-name" title={file.name}>
                {file.name}
                <small>{(file.size / 1024 / 1024).toFixed(2)} MB</small>
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--xs"
                disabled={disabled}
                onClick={() => onRemove(index)}
                aria-label={`Remove ${file.name}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {description && <div className="muted tiny pd-po-doc-picker__help">{description}</div>}
    </div>
  );
}

function ProductDrawer({ id, onClose }: { id?: string; onClose: () => void }) {
  useLockBodyScroll(true);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus the dialog when it opens and return focus to the selected product
  // when it closes. Keyboard focus remains inside the dialog while it is open.
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLButtonElement>('.drawer__header button')?.focus();
    return () => previousFocus?.focus();
  }, []);

  /*
   * Initial product request.
   *
   * No delivery address yet.
   * Gives us enabledLocations[].
   */
  const { data: productSummary, isLoading: productSummaryLoading } = useCustomerProduct(id);
  const [addressId, setAddressId] = useState('');
  const [text, setText] = useState('');
  const [poNumber, setPONumber] = useState('');
  const [poMode, setPoMode] = useState<'shared' | 'per-load'>('shared');
  const [perLoadPoNumbers, setPerLoadPoNumbers] = useState<string[]>([]);
  const [sharedPoDocuments, setSharedPoDocuments] = useState<File[]>([]);
  const [perLoadPoDocuments, setPerLoadPoDocuments] = useState<Record<number, File[]>>({});
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [isUploadingPoDocument, setIsUploadingPoDocument] = useState(false);
  const [quantity, setQuantity] = useState(0);
  // Held as a raw string so the field can be cleared/retyped; the effective
  // count is derived (and clamped to 1–50) from it.
  const [numberOfLoadsInput, setNumberOfLoadsInput] = useState('1');
  const numberOfLoads = Math.min(50, Math.max(1, Math.floor(Number(numberOfLoadsInput) || 1)));
  const [loadId, setLoadId] = useState('');
  const [palletId, setPalletId] = useState('');
  /*
   * Once the customer picks an ENABLED
   * location, get that address-specific
   * fulfilment configuration.
   */
  const {
    data: configuredProduct,
    isLoading: configuredProductLoading,
    isFetching: configuredProductFetching,
  } = useCustomerProduct(addressId ? id : undefined, addressId || undefined);
  const order = useCreateCustomerOrder();
  useEffect(() => {
    setAddressId('');
    setText('');
    setPONumber('');
    setPoMode('shared');
    setPerLoadPoNumbers([]);
    setSharedPoDocuments([]);
    setPerLoadPoDocuments({});
    setIsSubmittingOrder(false);
    setIsUploadingPoDocument(false);
    setQuantity(0);
    setNumberOfLoadsInput('1');
    setLoadId('');
    setPalletId('');
  }, [id]);
  const enabledLocations = useMemo(
    () =>
      sortLocations(
        Array.isArray(productSummary?.enabledLocations) ? productSummary.enabledLocations : []
      ),
    [productSummary?.enabledLocations]
  );
  useEffect(() => {
    setAddressId((current) => {
      if (current && enabledLocations.some((address) => address.id === current)) {
        return current;
      }
      const primary = enabledLocations.find((address) => address.isPrimary);
      if (primary) {
        return primary.id;
      }
      if (enabledLocations.length === 1) {
        return enabledLocations[0].id;
      }
      return '';
    });
  }, [enabledLocations]);
  const palletSizes = useMemo(
    () => (Array.isArray(productSummary?.palletSizes) ? productSummary.palletSizes : []),
    [productSummary?.palletSizes]
  );
  const loads = useMemo<CustomerProductLoadOption[]>(
    () => (Array.isArray(configuredProduct?.loadOptions) ? configuredProduct.loadOptions : []),
    [configuredProduct?.loadOptions]
  );
  const effectiveDefaultLoadOptionId =
    configuredProduct?.defaultFulfillmentMethodId ||
    configuredProduct?.organizationDefaultLoadOption?.id ||
    '';
  useEffect(() => {
    setPalletId((current) => {
      if (productSummary?.deliveryType !== 'PALLET') {
        return '';
      }
      if (current && palletSizes.some((pallet) => pallet.id === current)) {
        return current;
      }
      return palletSizes[0]?.id ?? '';
    });
  }, [productSummary?.deliveryType, palletSizes]);
  useEffect(() => {
    setLoadId((current) => {
      if (!addressId) {
        return '';
      }
      const availableIds = loads.map((load) => getLoadOptionId(load));
      if (current && availableIds.includes(current)) {
        return current;
      }
      if (effectiveDefaultLoadOptionId && availableIds.includes(effectiveDefaultLoadOptionId)) {
        return effectiveDefaultLoadOptionId;
      }
      if (loads.length === 1) {
        return getLoadOptionId(loads[0]);
      }
      return '';
    });
  }, [addressId, loads, effectiveDefaultLoadOptionId]);
  const selectedLoad = useMemo(
    () => loads.find((load) => getLoadOptionId(load) === loadId) ?? null,
    [loads, loadId]
  );
  /*
   * Quantity follows the selected
   * fulfilment-method capacity.
   *
   * This avoids coupling to the old
   * OrderableLoad helper shape.
   */
  useEffect(() => {
    if (!loadId) {
      setQuantity(0);
      return;
    }
    const capacity = Number(selectedLoad?.orgLoadOption?.capacityLabel);
    setQuantity(Number.isFinite(capacity) ? capacity : 0);
  }, [loadId, selectedLoad]);
  const hidePrice = !!productSummary?.hidePrice;
  const selectedAddress = enabledLocations.find((address) => address.id === addressId);
  /*
   * Ordering can be frozen per delivery location.
   *
   * Prefer the address-specific product response because it is the
   * authoritative response after a location is selected. Fall back to
   * the summary location so the warning appears immediately.
   */
  const configuredFreezeState = configuredProduct as
    | {
        location?: {
          onFreeze?: boolean;
          orderingContact?: OrderingContact | null;
        } | null;
        orderingContact?: OrderingContact | null;
      }
    | undefined;
  const isLocationFrozen =
    selectedAddress?.onFreeze === true || configuredFreezeState?.location?.onFreeze === true;
  const orderingContact: OrderingContact | null =
    configuredFreezeState?.orderingContact ??
    configuredFreezeState?.location?.orderingContact ??
    selectedAddress?.orderingContact ??
    null;
  const orderingContactEmail = String(orderingContact?.email ?? '').trim();
  const orderingContactLabel =
    orderingContact?.type === 'SALES_REP' ? 'sales representative' : 'organization administrator';
  const hasEnabledLocation = !!addressId && !!selectedAddress;
  const hasRequiredPallet = productSummary?.deliveryType !== 'PALLET' || Boolean(palletId);
  // This sub-location won't accept an order without a PO number.
  const requiresPo = Boolean(selectedAddress?.requiresPoNumber);
  const usesSeparatePoNumbers = numberOfLoads > 1 && poMode === 'per-load';
  const effectivePoNumbers = Array.from({ length: numberOfLoads }, (_, index) =>
    (usesSeparatePoNumbers ? (perLoadPoNumbers[index] ?? '') : poNumber).trim()
  );
  const missingPoLoadIndexes = requiresPo
    ? effectivePoNumbers.flatMap((value, index) => (value ? [] : [index + 1]))
    : [];
  const hasPoIfRequired = missingPoLoadIndexes.length === 0;
  const canOrder =
    hasEnabledLocation &&
    !isLocationFrozen &&
    Boolean(selectedLoad) &&
    hasRequiredPallet &&
    hasPoIfRequired &&
    !configuredProductLoading &&
    !configuredProductFetching;
  const selectedPrice =
    !hidePrice &&
    selectedLoad &&
    selectedLoad.price !== null &&
    Number.isFinite(Number(selectedLoad.price))
      ? Number(selectedLoad.price)
      : null;

  // Regional pricing estimate, shown only when the org has pricing on AND this
  // customer is opted in. No delivery date here, so fuel surcharge clamps to the
  // latest week (surchargeBasis="clamped").
  const { data: fuelSurchargeWeeks } = useFuelSurcharge();
  const pricingConfig = (configuredProduct as any)?.pricing;
  const pricingBreakdown = useMemo(() => {
    if (!pricingConfig?.enabled || !pricingConfig?.visibleToCustomer) return null;
    const weeks = fuelSurchargeWeeks ?? [];
    const week = weeks.find((w) => w.isCurrent) ?? weeks[weeks.length - 1] ?? null;
    const deliveryRateCents = pricingConfig.deliveryRateCents ?? 0;
    return computeOrderPricing({
      region: pricingConfig.region,
      tons: Number(quantity) || 0,
      deliveredPriceCents: pricingConfig.deliveredPriceCents,
      stonePriceCents: pricingConfig.stonePriceCents,
      deliveryRateCents,
      isPickup: selectedLoad?.orgLoadOption?.enableCarrierRate === false || deliveryRateCents === 0,
      tierDiscountCents: pricingConfig.tierDiscountCents,
      applyTierDiscount: pricingConfig.applyTierDiscount,
      materialPct: week?.materialPct ?? 0,
      deliveryPct: week?.deliveryPct ?? 0,
    });
  }, [pricingConfig, fuelSurchargeWeeks, quantity, selectedLoad]);

  const onSubmit = async () => {
    if (isSubmittingOrder) {
      return;
    }

    try {
      if (isLocationFrozen) {
        toast.warn(
          orderingContactEmail
            ? `Ordering for this delivery location is frozen. Please contact ${orderingContactEmail}.`
            : 'Ordering for this delivery location is frozen. Please contact your sales representative or organization administrator.'
        );

        return;
      }

      const loadOptionId = getLoadOptionId(selectedLoad);

      const blockReason = submitBlockReason({
        loadOptionId,
        addressId,
        deliveryType: productSummary?.deliveryType,
        palletId,
        requiresPo,
        poNumber: usesSeparatePoNumbers ? effectivePoNumbers[0] : poNumber,
      });

      if (missingPoLoadIndexes.length) {
        toast.error(
          `Enter a PO number for ${missingPoLoadIndexes.length === 1 ? 'load' : 'loads'} ${missingPoLoadIndexes.join(', ')}.`
        );
        return;
      }

      if (blockReason) {
        toast.error(blockReason);
        return;
      }

      if (!canOrder || !id) {
        return;
      }

      setIsSubmittingOrder(true);

      const placedMessage = numberOfLoads > 1 ? `${numberOfLoads} orders placed.` : 'Order placed.';

      /*
       * Every load is its own Order, with its own Order.poNumber.
       * Attachments are uploaded AFTER checkout succeeds:
       * - Shared mode: each shared file is attached to each created order.
       * - Per-load mode: each file is attached ONLY to its corresponding order.
       * Upload errors must never cause a second checkout / duplicate orders.
       */
      const documentsByLoad: File[][] = Array.from({ length: numberOfLoads }, (_, index) =>
        usesSeparatePoNumbers ? (perLoadPoDocuments[index] ?? []) : sharedPoDocuments
      );
      const selectedDocumentCount = documentsByLoad.reduce(
        (total, files) => total + files.length,
        0
      );

      const checkoutPayload = {
        ...buildCustomerOrderPayload({
          productId: id,
          loadOptionId,
          deliveryType: productSummary?.deliveryType,
          palletId,
          addressId,
          note: text,
          // The shared PO is copied to each newly created Order by the backend.
          poNumber: usesSeparatePoNumbers ? '' : poNumber,
          quantity,
          numberOfLoads,
        }),
        // Optional extension to the existing payload. Existing customers using
        // the shared PO option continue to send the original shape.
        ...(usesSeparatePoNumbers
          ? { poNumbers: effectivePoNumbers.map((value) => value || null) }
          : {}),
      };
      const created = await order.mutateAsync(checkoutPayload);

      if (selectedDocumentCount > 0) {
        const createdOrderIds = createdOrderIdsFromCheckoutResult(created);
        // The orderIds array is produced in the same order as the load creation
        // loop. Do not infer per-load identity from a potentially unordered
        // checkout.orders readback.
        const orderedIds = Array.isArray(created?.orderIds) ? created.orderIds : null;
        const hasReliableOrderMapping =
          !usesSeparatePoNumbers ||
          numberOfLoads === 1 ||
          (Array.isArray(orderedIds) &&
            orderedIds.length === numberOfLoads &&
            orderedIds.every((value: unknown, index: number) => value === createdOrderIds[index]));

        if (createdOrderIds.length !== numberOfLoads || !hasReliableOrderMapping) {
          toast.warn(
            `${numberOfLoads} order${numberOfLoads === 1 ? '' : 's'} created, but the PO documents ` +
              `could not be matched safely to their orders. Please attach them from each order's Documents panel.`,
            { autoClose: false }
          );
          onClose();
          return;
        }

        setIsUploadingPoDocument(true);
        const failedUploads: Array<{ load: number; fileName: string }> = [];
        let successfulUploads = 0;
        try {
          // Sequential uploads avoid opening dozens of concurrent requests
          // for a large checkout (up to 50 loads).
          for (let index = 0; index < createdOrderIds.length; index++) {
            for (const file of documentsByLoad[index]) {
              try {
                await uploadCustomerOrderDocument(createdOrderIds[index], file);
                successfulUploads++;
              } catch (uploadError) {
                failedUploads.push({ load: index + 1, fileName: file.name });
                console.error('PO document upload failed:', {
                  orderId: createdOrderIds[index],
                  fileName: file.name,
                  uploadError,
                });
              }
            }
          }
        } finally {
          setIsUploadingPoDocument(false);
        }

        if (failedUploads.length) {
          const examples = failedUploads
            .slice(0, 3)
            .map(({ load, fileName }) => `Load ${load}: ${fileName}`)
            .join('; ');
          toast.warn(
            `${numberOfLoads} order${numberOfLoads === 1 ? '' : 's'} created. ` +
              `${successfulUploads} document attachment${successfulUploads === 1 ? '' : 's'} uploaded; ` +
              `${failedUploads.length} failed (${examples}${failedUploads.length > 3 ? '; …' : ''}). ` +
              `Open the relevant order's Documents panel to retry the failed attachments. ` +
              `Do not place the order again.`,
            { autoClose: false }
          );
        } else {
          toast.success(
            `${numberOfLoads} order${numberOfLoads === 1 ? '' : 's'} placed and ` +
              `${successfulUploads} PO document attachment${successfulUploads === 1 ? '' : 's'} uploaded.`
          );
        }
        onClose();
        return;
      }

      toast.success(placedMessage);
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not submit.'));
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const header = (
    <div className="drawer__header">
      <h3
        id="customer-product-order-dialog-title"
        style={{
          margin: 0,
        }}
      >
        {productSummary?.name || 'Product'}
      </h3>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={onClose}
        disabled={isSubmittingOrder || isUploadingPoDocument}
      >
        Close
      </button>
    </div>
  );
  const body = productSummaryLoading ? (
    <div
      className="skeleton"
      style={{
        height: 260,
      }}
    />
  ) : !productSummary ? (
    <div className="pd-section pd-subtle">Not found.</div>
  ) : (
    <>
      <div className="pd-order-dialog__product">
        <img
          className="pd-hero"
          src={productSummary.imageUrl?.trim() || fallbackProductImage}
          alt={productSummary.name ?? 'Product'}
          onError={(e) => {
            e.currentTarget.src = fallbackProductImage;
          }}
        />
        <div className="pd-section pd-order-dialog__product-copy">
          <div className="pd-row">
            <div>
              <div className="pd-title">{productSummary.name}</div>
              <div className="pd-subtle">{productSummary.organizationName || '—'}</div>
              {productSummary.description && (
                <div
                  className="pd-subtle"
                  style={{
                    marginTop: 6,
                  }}
                >
                  {productSummary.description}
                </div>
              )}
            </div>
            <div className="pd-pills">
              <span className="pill">
                {productSummary.deliveryType === 'PALLET' ? 'Pallet' : 'Bulk'}
              </span>
              <span className="pill pill--ok">Enabled</span>
            </div>
          </div>
          <div
            className="muted tiny"
            style={{
              marginTop: 10,
            }}
          >
            Enabled for {enabledLocations.length} delivery{' '}
            {enabledLocations.length === 1 ? 'location' : 'locations'}.
          </div>
        </div>
      </div>
      <div className="pd-section">
        <div
          className={`pd-form-grid ${productSummary.deliveryType === 'PALLET' ? 'has-pallet' : ''}`}
        >
          {enabledLocations.length === 0 && (
            <div
              className="pd-freeze-banner pd-freeze-banner--grid"
              role="status"
              style={{ gridColumn: '1 / -1' }}
            >
              <div className="pd-freeze-banner__icon" aria-hidden="true">
                !
              </div>
              <div className="pd-freeze-banner__content">
                <div className="pd-freeze-banner__title">
                  No delivery locations are enabled for this product
                </div>
                <div className="pd-freeze-banner__message">
                  Contact your sales representative or organization administrator if you need access
                  to this product at a delivery location.
                </div>
              </div>
            </div>
          )}
          <div className="form-control fc-deliver">
            <label>Deliver to</label>
            <select
              className="input-lg"
              value={addressId}
              onChange={(event) => {
                setAddressId(event.target.value);
                setLoadId('');
              }}
              disabled={enabledLocations.length === 0}
            >
              {enabledLocations.length === 0 ? (
                <option value="">No enabled delivery locations</option>
              ) : (
                enabledLocations.length > 1 && <option value="">Select delivery location…</option>
              )}
              {enabledLocations.map((address) => (
                <option key={address.id} value={address.id}>
                  {formatLocation(address)}
                  {address.onFreeze ? ' • Ordering frozen' : ''}
                </option>
              ))}
            </select>
          </div>
          {productSummary.deliveryType === 'PALLET' && (
            <div className="form-control fc-pallet">
              <label>Pallet size</label>
              <select
                className="input-lg"
                value={palletId}
                onChange={(event) => setPalletId(event.target.value)}
                disabled={!palletSizes.length}
              >
                {!palletSizes.length && <option value="">No pallet sizes</option>}
                {palletSizes.map((palletSize: any) => (
                  <option key={palletSize.id} value={palletSize.id}>
                    {`${palletSize?.template?.name ?? ''} - ${palletSize?.template?.type ?? ''} - `}
                    {palletSize?.template?.lengthIn}×{palletSize?.template?.widthIn}×
                    {palletSize?.template?.heightIn}
                    {palletSize?.template?.weightTons
                      ? ` - ${palletSize.template.weightTons} t`
                      : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="form-control fc-load">
            <label>Fulfilment Method</label>
            <select
              className="input-lg"
              value={loadId}
              onChange={(event) => setLoadId(event.target.value)}
              disabled={
                !addressId ||
                configuredProductLoading ||
                configuredProductFetching ||
                loads.length === 0
              }
            >
              {!addressId ? (
                <option value="">Select delivery location first</option>
              ) : configuredProductLoading || configuredProductFetching ? (
                <option value="">Loading fulfilment methods…</option>
              ) : loads.length === 0 ? (
                <option value="">No fulfilment methods available</option>
              ) : (
                <option value="">Select Fulfilment Method…</option>
              )}
              {loads.map((load) => {
                const optionId = getLoadOptionId(load);
                const isDefault = optionId === effectiveDefaultLoadOptionId;
                const hasPrice =
                  !hidePrice && load.price !== null && Number.isFinite(Number(load.price));
                return (
                  <option key={optionId} value={optionId}>
                    {load.orgLoadOption?.name ?? optionId}
                    {load.orgLoadOption?.capacityLabel
                      ? ` - ${load.orgLoadOption.capacityLabel}t`
                      : ''}
                    {hasPrice ? ` • ${fmtMoney(Number(load.price), load.currency || 'USD')}` : ''}
                    {isDefault
                      ? load.isOrganizationDefault
                        ? ' • Organization default'
                        : ' • Default'
                      : ''}
                  </option>
                );
              })}
            </select>
            {addressId &&
              !configuredProductLoading &&
              !configuredProductFetching &&
              loads.length === 0 && (
                <div
                  className="muted tiny"
                  style={{
                    marginTop: 6,
                  }}
                >
                  No fulfilment method is configured for this delivery location.
                </div>
              )}
          </div>
          {isLocationFrozen && (
            <div className="pd-freeze-banner pd-freeze-banner--grid" role="alert">
              <div className="pd-freeze-banner__icon" aria-hidden="true">
                !
              </div>
              <div className="pd-freeze-banner__content">
                <div className="pd-freeze-banner__title">
                  Ordering is frozen for this delivery location
                </div>
                <div className="pd-freeze-banner__message">
                  Orders to this location are currently on hold. Please contact your{' '}
                  {orderingContactLabel}
                  {orderingContactEmail ? (
                    <>
                      {' '}
                      at{' '}
                      <a
                        className="pd-freeze-banner__email"
                        href={`mailto:${orderingContactEmail}`}
                      >
                        {orderingContactEmail}
                      </a>
                    </>
                  ) : null}{' '}
                  to resume ordering.
                </div>
              </div>
            </div>
          )}
          <div className="form-control fc-qty">
            <label>Quantity (t)</label>
            <input
              className="input-lg"
              type="number"
              min="0"
              step="0.01"
              value={quantity}
              onChange={(event) => setQuantity(parseQuantityInput(event.target.value))}
              placeholder="Enter quantity"
            />
          </div>
          <div className="form-control fc-qty">
            <label>Number of loads</label>
            <input
              className="input-lg"
              type="number"
              min="1"
              max="50"
              step="1"
              value={numberOfLoadsInput}
              // Accept any transient text (including empty) while typing; snap to
              // the clamped value only on blur so the field stays editable.
              onChange={(event) => setNumberOfLoadsInput(event.target.value)}
              onBlur={() => setNumberOfLoadsInput(String(numberOfLoads))}
            />
          </div>
          <div className="form-control fc-po fc-po--multi">
            <label className="pd-po-heading">Purchase order information</label>

            {numberOfLoads > 1 && (
              <fieldset className="pd-po-choice" disabled={isSubmittingOrder}>
                <legend className="muted tiny">PO number assignment</legend>
                <label className="pd-po-choice__option">
                  <input
                    type="radio"
                    name="checkout-po-mode"
                    checked={!usesSeparatePoNumbers}
                    onChange={() => setPoMode('shared')}
                  />
                  <span>Use the same PO number for all {numberOfLoads} loads</span>
                </label>
                <label className="pd-po-choice__option">
                  <input
                    type="radio"
                    name="checkout-po-mode"
                    checked={usesSeparatePoNumbers}
                    onChange={() => {
                      // Keep a PO already entered in the single/shared field.
                      // Do not overwrite an existing Load 1 PO if the user toggles modes.
                      setPerLoadPoNumbers((current) => {
                        if (current[0]?.trim() || !poNumber.trim()) return current;
                        const next = [...current];
                        next[0] = poNumber;
                        return next;
                      });
                      // Keep selected shared PO files with Load 1, unless Load 1
                      // already has its own files. Do not silently fan them out
                      // to every separate PO / order.
                      setPerLoadPoDocuments((current) =>
                        (current[0]?.length ?? 0) > 0 || sharedPoDocuments.length === 0
                          ? current
                          : { ...current, 0: [...sharedPoDocuments] }
                      );
                      setPoMode('per-load');
                    }}
                  />
                  <span>Enter a separate PO number for each load</span>
                </label>
              </fieldset>
            )}

            {usesSeparatePoNumbers ? (
              <div className="pd-po-individual">
                <div className="pd-po-individual__header">
                  <span className="muted tiny">
                    {requiresPo ? 'Required for every load' : 'Optional for each load'}
                  </span>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={isSubmittingOrder}
                    onClick={() => {
                      const first = perLoadPoNumbers[0] ?? '';
                      setPerLoadPoNumbers(Array.from({ length: numberOfLoads }, () => first));
                    }}
                  >
                    Copy first PO to all
                  </button>
                </div>
                <div className="pd-po-individual__grid">
                  {Array.from({ length: numberOfLoads }, (_, index) => (
                    <div className="pd-po-load-row" key={index}>
                      <div className="pd-po-load-row__heading">
                        <strong>Load {index + 1}</strong>
                        <span className="muted tiny">Separate order</span>
                      </div>
                      <div className="form-control pd-po-load-row__number">
                        <label htmlFor={`checkout-po-${index}`}>
                          PO number {requiresPo ? '(required)' : '(optional)'}
                        </label>
                        <input
                          id={`checkout-po-${index}`}
                          className="input-lg"
                          type="text"
                          value={perLoadPoNumbers[index] ?? ''}
                          onChange={(event) =>
                            setPerLoadPoNumbers((current) => {
                              const next = [...current];
                              next[index] = event.target.value;
                              return next;
                            })
                          }
                          placeholder={`PO for load ${index + 1}`}
                          aria-invalid={requiresPo && !(perLoadPoNumbers[index] ?? '').trim()}
                          disabled={isSubmittingOrder}
                        />
                      </div>
                      <PoDocumentsPicker
                        id={`checkout-po-docs-${index}`}
                        files={perLoadPoDocuments[index] ?? []}
                        disabled={isSubmittingOrder}
                        description={`Only attached to Load ${index + 1}'s order · 15 MB max per file`}
                        onAdd={(files) =>
                          setPerLoadPoDocuments((current) => ({
                            ...current,
                            [index]: [...(current[index] ?? []), ...files],
                          }))
                        }
                        onRemove={(fileIndex) =>
                          setPerLoadPoDocuments((current) => ({
                            ...current,
                            [index]: (current[index] ?? []).filter((_, i) => i !== fileIndex),
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="form-control pd-po-shared">
                <div className="pd-po-shared__number">
                  <label htmlFor="checkout-shared-po">
                    PO Number ({requiresPo ? 'required' : 'optional'})
                  </label>
                  <input
                    id="checkout-shared-po"
                    className="input-lg"
                    type="text"
                    value={poNumber}
                    onChange={(event) => setPONumber(event.target.value)}
                    placeholder="Enter PO number"
                    disabled={isSubmittingOrder}
                  />
                  {numberOfLoads > 1 && (
                    <div className="muted tiny">This PO number applies to every load.</div>
                  )}
                </div>
                <PoDocumentsPicker
                  id={`checkout-shared-po-docs-${id ?? 'order'}`}
                  files={sharedPoDocuments}
                  disabled={isSubmittingOrder}
                  description={
                    numberOfLoads > 1
                      ? 'Each file is attached to every order · 15 MB max per file'
                      : 'Attached to this order · 15 MB max per file'
                  }
                  onAdd={(files) => setSharedPoDocuments((current) => [...current, ...files])}
                  onRemove={(index) =>
                    setSharedPoDocuments((current) => current.filter((_, i) => i !== index))
                  }
                />
              </div>
            )}

            {requiresPo && !hasPoIfRequired && (
              <div className="pd-po-warning" role="alert">
                {usesSeparatePoNumbers
                  ? `PO required for load${missingPoLoadIndexes.length === 1 ? '' : 's'} ${missingPoLoadIndexes.join(', ')}.`
                  : 'A PO number is required for this delivery location.'}
              </div>
            )}
          </div>
          <div className="form-control pd-note-field">
            <label className="muted tiny">Add a note</label>
            <textarea
              value={text}
              rows={4}
              onChange={(event) => setText(event.target.value)}
              placeholder="Type a message…"
              style={{
                width: '100%',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                padding: 10,
                marginTop: 6,
              }}
            />
          </div>

          {pricingBreakdown && (
            // Full width: span every grid column so the estimate table has room
            // and doesn't scroll inside a narrow cell.
            <div className="pd-pricing-estimate">
              <PricingBreakdownView breakdown={pricingBreakdown} surchargeBasis="clamped" />
            </div>
          )}
        </div>
      </div>
    </>
  );

  const footer = productSummary ? (
    <div className="pd-footer pd-order-dialog__footer">
      <div className="pd-subtotal">
        {!hidePrice && selectedPrice !== null && selectedLoad ? (
          <>
            Subtotal: <strong>{fmtMoney(selectedPrice, selectedLoad.currency || 'USD')}</strong>
          </>
        ) : (
          <span />
        )}
      </div>
      <button
        type="button"
        className="btn btn--primary"
        onClick={onSubmit}
        disabled={isSubmittingOrder || !canOrder}
        title={
          isLocationFrozen
            ? orderingContactEmail
              ? `Ordering is frozen for this location. Contact ${orderingContactEmail}.`
              : 'Ordering is frozen for this location. Contact your sales representative or organization administrator.'
            : !addressId
              ? 'Select a delivery location'
              : !selectedLoad
                ? 'Select a fulfilment method'
                : productSummary.deliveryType === 'PALLET' && !palletId
                  ? 'Select a pallet size'
                  : requiresPo && !hasPoIfRequired
                    ? usesSeparatePoNumbers
                      ? `A PO number is required for loads ${missingPoLoadIndexes.join(', ')}`
                      : 'A PO number is required for this location'
                    : 'Place order'
        }
      >
        {isUploadingPoDocument
          ? 'Uploading PO…'
          : isSubmittingOrder
            ? 'Submitting…'
            : 'Place order'}
      </button>
    </div>
  ) : null;

  const isBusy = isSubmittingOrder || isUploadingPoDocument;
  const onDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      if (!isBusy) onClose();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
          'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="drawer pd-order-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-product-order-dialog-title"
      onKeyDown={onDialogKeyDown}
    >
      <div
        className="drawer__backdrop"
        onClick={() => {
          if (!isBusy) onClose();
        }}
      />
      <div ref={dialogRef} className="drawer__panel pd-drawer">
        {header}
        <div className="drawer__content">{body}</div>
        {footer}
      </div>
    </div>,
    document.body
  );
}
function PreviewProductRequestDialog({
  product,
  isSubmitting,
  onClose,
  onConfirm,
}: {
  product: CustomerProductCard;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  useLockBodyScroll(true);
  return createPortal(
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="preview-request-title">
      <div className="modal__backdrop" onClick={onClose} />
      <div
        className="modal__panel"
        style={{
          width: '100%',
          maxWidth: 520,
        }}
      >
        <div className="modal__header">
          <h3 id="preview-request-title" className="modal__title">
            Request product
          </h3>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Close
          </button>
        </div>
        <div className="modal__body">
          <img
            src={product.imageUrl?.trim() || fallbackProductImage}
            alt={product.name || 'Product'}
            style={{
              display: 'block',
              width: 96,
              height: 96,
              marginBottom: 14,
              borderRadius: 12,
              objectFit: 'cover',
            }}
            onError={(e) => {
              e.currentTarget.src = fallbackProductImage;
            }}
          />
          <p
            style={{
              marginTop: 0,
            }}
          >
            <strong>{product.name}</strong> is currently available for preview, but it has not yet
            been enabled for purchase.
          </p>
          <p className="muted">
            Would you like to request this product? Your representative will be notified. They can
            review your account and decide which delivery location or locations should have this
            product enabled.
          </p>
        </div>
        <div className="modal__footer">
          <div
            className="actions"
            style={{
              gap: 8,
            }}
          >
            <button
              type="button"
              className="btn btn--primary"
              onClick={onConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending request…' : 'Yes, request product'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
